// Projects stable transcript identities and resolves registry-owned compound
// state before any activity summary or customer-visible node is selected.
import { type ReactNode } from 'react';

import {
    type KnownEventType,
    type RawCommittedEvent,
    type RawPendingEvent,
    type Uuid,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type TranscriptState } from '@apps/web/features/phoebe_chat_v3/model/state';
import { composeDefinitionModules } from '@apps/web/features/phoebe_chat_v3/registry/compose_registry';
import { assistantTextPayloadSchema } from '@apps/web/features/phoebe_chat_v3/registry/definitions/assistant_text';
import { generatedDefinitionModules } from '@apps/web/features/phoebe_chat_v3/registry/generated_registry_input';
import {
    type AnyMemberMaterialization,
    type AnyParsedSlotMember,
    type CompoundSlotDeclaration,
    type CompoundSlotName,
    type LifecycleEvidenceDelta,
    type MessageSender,
    reduceParsedSlotMember,
    type RegistryOccurrence,
    type RegistryRuntimeIssue,
    type RenderMaterialization,
    type ResolvedActivityGroup,
    type SafeRenderContext,
    type SlotKey,
    type ToolCompoundState,
} from '@apps/web/features/phoebe_chat_v3/registry/types';

export type ProjectedTranscriptRow =
    | Readonly<{
          readonly kind: 'committed';
          readonly occurrenceId: string;
          readonly eventId: Uuid;
      }>
    | Readonly<{
          readonly kind: 'pending';
          readonly occurrenceId: string;
          readonly pendingEventId: Uuid;
      }>
    | Readonly<{
          readonly kind: 'optimistic';
          readonly occurrenceId: `local-operation:${string}`;
          readonly operationId: Uuid;
      }>;

export type TranscriptProjection = Readonly<{
    readonly rows: ReadonlyArray<ProjectedTranscriptRow>;
    readonly rowIndexByOccurrenceId: ReadonlyMap<string, number>;
}>;

export type QueuedUserMessage = Readonly<{
    readonly occurrenceId: string;
    readonly text: string;
}>;

function exactRetainedOperationId(
    externalReference: string | null,
    indexedOperationId: Uuid | undefined,
    state: TranscriptState,
): Uuid | null {
    // Exact equality is an identity boundary: a loose prefix or suffix match
    // can merge two different people's messages into one rendered row.
    return indexedOperationId !== undefined &&
        externalReference === `api_chat_send:${indexedOperationId}` &&
        state.optimisticByOperationId.has(indexedOperationId)
        ? indexedOperationId
        : null;
}

function retainedOperationId(
    indexedOperationId: Uuid | undefined,
    state: TranscriptState,
): Uuid | null {
    return indexedOperationId !== undefined &&
        state.optimisticByOperationId.has(indexedOperationId)
        ? indexedOperationId
        : null;
}

export function projectTranscript(
    state: TranscriptState,
): TranscriptProjection {
    const rows: Array<ProjectedTranscriptRow> = [];
    // When each row happened, so a pending receipt can be pinned among the
    // committed rows. A row whose time is unknown never displaces one.
    const rowCreatedAtMs: Array<number> = [];
    const projectedOperationIds = new Set<Uuid>();
    const committedSourcePendingIds = new Set<Uuid>();

    for (const eventId of state.committedOrder) {
        const committed = state.committedById.get(eventId);
        if (committed === undefined) {
            rows.push({
                kind: 'committed',
                occurrenceId: `committed-event:${eventId}`,
                eventId,
            });
            rowCreatedAtMs.push(Number.NaN);
            continue;
        }
        const directOperationId = exactRetainedOperationId(
            committed.external_ref,
            state.operationIdByCommittedId.get(eventId),
            state,
        );
        const sourcePendingId = committed.source_pending_event_id;
        const pendingOperationId =
            sourcePendingId === null
                ? null
                : retainedOperationId(
                      state.operationIdByPendingId.get(sourcePendingId),
                      state,
                  );
        if (
            directOperationId !== null &&
            pendingOperationId !== null &&
            directOperationId !== pendingOperationId
        ) {
            throw new Error(
                `Committed event ${eventId} resolved conflicting local ` +
                    'operations.',
            );
        }
        const operationId = directOperationId ?? pendingOperationId;
        if (operationId !== null) {
            projectedOperationIds.add(operationId);
        }
        if (sourcePendingId !== null) {
            committedSourcePendingIds.add(sourcePendingId);
        }
        rows.push({
            kind: 'committed',
            occurrenceId:
                operationId === null
                    ? `committed-event:${eventId}`
                    : `local-operation:${operationId}`,
            eventId,
        });
        rowCreatedAtMs.push(Date.parse(committed.created_at));
    }

    for (const pendingEventId of state.pendingOrder) {
        const pending = state.pendingById.get(pendingEventId);
        const committedId =
            state.committedIdBySourcePendingId.get(pendingEventId);
        if (
            pending === undefined ||
            committedSourcePendingIds.has(pendingEventId) ||
            (committedId !== undefined &&
                state.committedById.has(committedId)) ||
            (pending.status === 'drained' &&
                pending.drained_into_event_id === null)
        ) {
            continue;
        }
        const operationId = exactRetainedOperationId(
            pending.external_ref,
            state.operationIdByPendingId.get(pendingEventId),
            state,
        );
        if (operationId !== null) {
            projectedOperationIds.add(operationId);
        }
        // A receipt is pinned to the moment it was scheduled: it sits after
        // every committed row that had already happened and before the
        // first one that happened later, so the steps and answers that
        // follow read below it. Every other pending row still trails the
        // whole committed transcript.
        const createdAtMs = RECEIPT_EVENT_TYPES.has(pending.type)
            ? Date.parse(pending.created_at)
            : Number.POSITIVE_INFINITY;
        const insertAt = rowCreatedAtMs.findIndex(
            (rowMs) => rowMs > createdAtMs,
        );
        const row: ProjectedTranscriptRow = {
            kind: 'pending',
            occurrenceId:
                operationId === null
                    ? `pending-event:${pendingEventId}`
                    : `local-operation:${operationId}`,
            pendingEventId,
        };
        if (insertAt === -1) {
            rows.push(row);
            rowCreatedAtMs.push(createdAtMs);
            continue;
        }
        rows.splice(insertAt, 0, row);
        rowCreatedAtMs.splice(insertAt, 0, createdAtMs);
    }

    const optimisticMessages = Array.from(
        state.optimisticByOperationId.values(),
    ).sort(
        (left, right) =>
            left.createdAtMs - right.createdAtMs ||
            left.operationId.localeCompare(right.operationId),
    );
    for (const message of optimisticMessages) {
        if (
            projectedOperationIds.has(message.operationId) ||
            message.matchedPendingId !== null ||
            message.matchedCommittedId !== null
        ) {
            continue;
        }
        rows.push({
            kind: 'optimistic',
            occurrenceId: message.occurrenceId,
            operationId: message.operationId,
        });
    }

    const rowIndexByOccurrenceId = new Map<string, number>();
    for (const [index, row] of rows.entries()) {
        rowIndexByOccurrenceId.set(row.occurrenceId, index);
    }
    return { rows, rowIndexByOccurrenceId };
}

const ACTIVITY_REGISTRY = composeDefinitionModules(generatedDefinitionModules);

/**
 * Event types the registry declares as receipts (today only `poke`). A
 * receipt row stands on its own in every phase, so a pending one is pinned
 * where it belongs in time rather than sorted after the whole transcript.
 */
const RECEIPT_EVENT_TYPES: ReadonlySet<string> = new Set(
    Object.entries(ACTIVITY_REGISTRY)
        .filter(
            ([, definition]) =>
                definition.disposition === 'render' &&
                definition.group?.receipt === true,
        )
        .map(([eventType]) => eventType),
);

/**
 * Projects user messages that have reached the client or backend but have not
 * yet become durable transcript events. The registry schema remains the
 * customer-visible payload boundary for server-pending rows.
 */
export function projectQueuedUserMessages(
    state: TranscriptState,
): ReadonlyArray<QueuedUserMessage> {
    const messages: Array<QueuedUserMessage> = [];
    for (const pendingEventId of state.pendingOrder) {
        const pending = state.pendingById.get(pendingEventId);
        const committedId =
            state.committedIdBySourcePendingId.get(pendingEventId);
        if (
            pending === undefined ||
            pending.type !== 'user_message' ||
            pending.status !== 'pending' ||
            (committedId !== undefined && state.committedById.has(committedId))
        ) {
            continue;
        }
        const parsed = ACTIVITY_REGISTRY.user_message.schema.safeParse(
            pending.payload,
        );
        if (!parsed.success || parsed.data.display_only === true) {
            continue;
        }
        const operationId = exactRetainedOperationId(
            pending.external_ref,
            state.operationIdByPendingId.get(pendingEventId),
            state,
        );
        if (
            operationId !== null &&
            state.optimisticByOperationId.get(operationId)?.sentWhileBusy ===
                false
        ) {
            continue;
        }
        messages.push({
            occurrenceId:
                operationId === null
                    ? `pending-event:${pendingEventId}`
                    : `local-operation:${operationId}`,
            text: parsed.data.text,
        });
    }
    const optimisticMessages = Array.from(
        state.optimisticByOperationId.values(),
    ).sort(
        (left, right) =>
            left.createdAtMs - right.createdAtMs ||
            left.operationId.localeCompare(right.operationId),
    );
    for (const optimistic of optimisticMessages) {
        if (
            !optimistic.sentWhileBusy ||
            optimistic.status === 'failed' ||
            optimistic.matchedPendingId !== null ||
            optimistic.matchedCommittedId !== null
        ) {
            continue;
        }
        messages.push({
            occurrenceId: optimistic.occurrenceId,
            text: optimistic.text,
        });
    }
    return messages;
}

const MAX_PROJECTION_ISSUES = 50;

export type ActivityProjectionRegistry = typeof ACTIVITY_REGISTRY;

export type ProjectedActivityOccurrence = Readonly<{
    readonly kind: 'activity_occurrence';
    readonly rowId: string;
    readonly occurrenceId: string;
    readonly eventIds: ReadonlyArray<Uuid>;
    readonly day: string;
    readonly createdAtMs: number;
    readonly node: ReactNode;
    readonly group: ResolvedActivityGroup<ToolCompoundState | null>;
}>;

export type ProjectedOrdinaryOccurrence = Readonly<{
    readonly kind: 'ordinary_occurrence';
    readonly rowId: string;
    readonly occurrenceId: string;
    readonly eventIds: ReadonlyArray<Uuid>;
    readonly day: string;
    readonly node: ReactNode;
    /** Set for plain chat messages so the transcript can group a sender's run. */
    readonly messageSender?: MessageSender;
    readonly messageText?: string;
}>;

function messageSenderFor(
    eventType: KnownEventType,
    source: OccurrenceSource,
    context: SafeRenderContext,
): MessageSender | null {
    switch (eventType) {
        case 'assistant_text':
            return 'assistant';
        case 'user_message': {
            // A message with no resolved sender reads as the viewer's own,
            // matching how the primitive attributes it.
            const userId =
                source.committed?.sent_by?.user_id ??
                source.pending?.enqueued_by_user_id ??
                context.scope.userId;
            return `user:${userId}`;
        }
        default:
            return null;
    }
}

export type ProjectedPlaceholderOccurrence = Readonly<{
    readonly kind: 'placeholder';
    readonly rowId: string;
    readonly occurrenceId: string;
    readonly eventIds: ReadonlyArray<Uuid>;
    readonly day: string;
    readonly customerCopy: string;
}>;

/**
 * Why a row renders nothing. Activity folds across an intentionally
 * invisible event; an unknown or malformed one is a safety boundary the
 * group cannot vouch for, so it ends the group instead.
 */
export type ActivityBoundaryReason = 'invisible' | 'unknown' | 'malformed';

export type ProjectedActivityBoundary = Readonly<{
    readonly kind: 'boundary';
    readonly reason: ActivityBoundaryReason;
    readonly rowId: string;
    readonly occurrenceId: string;
    readonly eventIds: ReadonlyArray<Uuid>;
    readonly day: string;
}>;

export type ProjectedActivityInput =
    | ProjectedActivityOccurrence
    | ProjectedOrdinaryOccurrence
    | ProjectedPlaceholderOccurrence
    | ProjectedActivityBoundary;

export type ActivityProjectionIssue = Readonly<{
    readonly reason:
        | 'known_payload_invalid'
        | 'missing_customer_narration'
        | 'orphan_slot'
        | 'unknown_event_type';
    readonly eventType: string | null;
    readonly count: 1;
}>;

export type CompoundProtocolConflict = Readonly<{
    readonly reason: 'terminal_slot_conflict';
    readonly slot: CompoundSlotName;
}>;

export type ActivityOccurrenceProjection = Readonly<{
    readonly occurrences: ReadonlyArray<ProjectedActivityInput>;
    readonly compoundStateByOwnerOccurrenceId: ReadonlyMap<
        string,
        ToolCompoundState
    >;
    readonly lifecycleEvidence: ReadonlyArray<LifecycleEvidenceDelta>;
    readonly issues: ReadonlyArray<ActivityProjectionIssue>;
    readonly protocolConflicts: ReadonlyArray<CompoundProtocolConflict>;
}>;

export type ActivityProjectionIssueRecorder = (
    issue: ActivityProjectionIssue,
) => void;

type OccurrenceSource = Readonly<{
    readonly committed: RawCommittedEvent | null;
    readonly pending: RawPendingEvent | null;
    readonly payload: unknown;
    readonly eventType: string;
    readonly createdAt: string;
    readonly eventIds: ReadonlyArray<Uuid>;
}>;

type ToolOwnerRecord = Readonly<{
    readonly occurrence: RegistryOccurrence<'tool_call'>;
    readonly source: OccurrenceSource;
    readonly state: ToolCompoundState;
}>;

// A message sent while a card was pending retires that card on the client
// at once. The overlay holds while the send is alive and the slot still
// waits; a committed terminal member (the server's expiry, or a decision that
// won the race) is never overlaid, and a failed send lifts it.
function applyLocalSupersession(
    state: TranscriptState,
    ownerByOccurrenceId: Map<string, ToolOwnerRecord>,
): void {
    const supersededCommandIds = new Set<string>();
    for (const optimistic of state.optimisticByOperationId.values()) {
        if (
            optimistic.status === 'failed' ||
            optimistic.matchedCommittedId !== null
        ) {
            continue;
        }
        for (const commandId of optimistic.supersededCommandIds) {
            supersededCommandIds.add(commandId);
        }
    }
    if (supersededCommandIds.size === 0) {
        return;
    }
    for (const [occurrenceId, owner] of ownerByOccurrenceId) {
        const { approval, question } = owner.state;
        const expiredApproval =
            approval?.kind === 'approval_required' &&
            supersededCommandIds.has(approval.commandId)
                ? {
                      kind: 'approval_resolved' as const,
                      commandId: approval.commandId,
                      decision: 'expired' as const,
                      presentation: approval.presentation,
                      ...(approval.editor === null ||
                      approval.editor === undefined
                          ? {}
                          : { editor: approval.editor }),
                  }
                : null;
        const expiredQuestion =
            question?.kind === 'question_required' &&
            supersededCommandIds.has(question.commandId)
                ? {
                      kind: 'question_resolved' as const,
                      commandId: question.commandId,
                      resolution: 'expired' as const,
                      answer: null,
                  }
                : null;
        if (expiredApproval === null && expiredQuestion === null) {
            continue;
        }
        ownerByOccurrenceId.set(occurrenceId, {
            ...owner,
            state: {
                ...owner.state,
                approval: expiredApproval ?? approval,
                question: expiredQuestion ?? question,
            },
        });
    }
}

type ProjectedMember = Readonly<{
    readonly eventType: KnownEventType;
    readonly member: AnyParsedSlotMember;
}>;

function occurrenceSource(
    row: ProjectedTranscriptRow,
    state: TranscriptState,
): OccurrenceSource | null {
    switch (row.kind) {
        case 'committed': {
            const committed = state.committedById.get(row.eventId);
            if (committed === undefined) {
                return null;
            }
            return {
                committed,
                pending: null,
                payload: committed.payload,
                eventType: committed.type,
                createdAt: committed.created_at,
                eventIds: [committed.id],
            };
        }
        case 'pending': {
            const pending = state.pendingById.get(row.pendingEventId);
            if (pending === undefined) {
                return null;
            }
            return {
                committed: null,
                pending,
                payload: pending.payload,
                eventType: pending.type,
                createdAt: pending.created_at,
                eventIds: [pending.id],
            };
        }
        case 'optimistic': {
            const optimistic = state.optimisticByOperationId.get(
                row.operationId,
            );
            if (optimistic === undefined) {
                return null;
            }
            return {
                committed: null,
                pending: null,
                payload: { text: optimistic.text },
                eventType: 'user_message',
                createdAt: new Date(optimistic.createdAtMs).toISOString(),
                eventIds: [optimistic.operationId],
            };
        }
        default: {
            const exhaustiveRow: never = row;
            return exhaustiveRow;
        }
    }
}

function registryOccurrence<K extends KnownEventType>(
    row: ProjectedTranscriptRow,
    source: OccurrenceSource,
    type: K,
): RegistryOccurrence<K> {
    let phase: RegistryOccurrence<K>['phase'];
    switch (row.kind) {
        case 'committed':
            phase = 'committed';
            break;
        case 'optimistic':
            phase = 'optimistic';
            break;
        case 'pending':
            switch (source.pending?.status) {
                case 'drained':
                    phase = 'reconciling';
                    break;
                case 'rejected':
                    phase = 'rejected';
                    break;
                case 'pending':
                default:
                    phase = 'pending';
                    break;
            }
            break;
        default: {
            const exhaustiveRow: never = row;
            return exhaustiveRow;
        }
    }
    return {
        occurrenceId: row.occurrenceId,
        type,
        phase,
        committed: source.committed,
        pending: source.pending,
    };
}

function slotKey(key: SlotKey): string {
    return `${key.kind}:${key.value}`;
}

function organizationDay(isoDateTime: string, timeZone: string): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date(isoDateTime));
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (year === undefined || month === undefined || day === undefined) {
        throw new Error('Could not project the organization-local day.');
    }
    return `${year}-${month}-${day}`;
}

function occurrenceCreatedAtMs(createdAt: string): number {
    const parsed = Date.parse(createdAt);
    return Number.isFinite(parsed) ? parsed : 0;
}

function hasKnownEventType(eventType: string): eventType is KnownEventType {
    return Object.prototype.hasOwnProperty.call(ACTIVITY_REGISTRY, eventType);
}

function materializeGroupedStandalone(
    registry: ActivityProjectionRegistry,
    eventType: KnownEventType,
    row: ProjectedTranscriptRow,
    source: OccurrenceSource,
    context: SafeRenderContext,
): RenderMaterialization<null> | null {
    switch (eventType) {
        case 'assistant_reasoning':
            return registry.assistant_reasoning.materialize(
                source.payload,
                registryOccurrence(row, source, 'assistant_reasoning'),
                context,
            );
        case 'child_completed':
            return registry.child_completed.materialize(
                source.payload,
                registryOccurrence(row, source, 'child_completed'),
                context,
            );
        case 'item_compaction':
            return registry.item_compaction.materialize(
                source.payload,
                registryOccurrence(row, source, 'item_compaction'),
                context,
            );
        case 'poke':
            return registry.poke.materialize(
                source.payload,
                registryOccurrence(row, source, 'poke'),
                context,
            );
        default:
            return null;
    }
}

function materializeOrdinaryStandalone(
    registry: ActivityProjectionRegistry,
    eventType: KnownEventType,
    row: ProjectedTranscriptRow,
    source: OccurrenceSource,
    context: SafeRenderContext,
): RenderMaterialization<null> | null {
    switch (eventType) {
        case 'assistant_text':
            return registry.assistant_text.materialize(
                source.payload,
                registryOccurrence(row, source, 'assistant_text'),
                context,
            );
        case 'interrupt_requested':
            return registry.interrupt_requested.materialize(
                source.payload,
                registryOccurrence(row, source, 'interrupt_requested'),
                context,
            );
        case 'run_failed':
            return registry.run_failed.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_failed'),
                context,
            );
        case 'status_update':
            return registry.status_update.materialize(
                source.payload,
                registryOccurrence(row, source, 'status_update'),
                context,
            );
        case 'stop_requested':
            return registry.stop_requested.materialize(
                source.payload,
                registryOccurrence(row, source, 'stop_requested'),
                context,
            );
        case 'outreach_runner_kickoff':
            return registry.outreach_runner_kickoff.materialize(
                source.payload,
                registryOccurrence(row, source, 'outreach_runner_kickoff'),
                context,
            );
        case 'item_compaction':
            return registry.item_compaction.materialize(
                source.payload,
                registryOccurrence(row, source, 'item_compaction'),
                context,
            );
        case 'user_message':
            return registry.user_message.materialize(
                source.payload,
                registryOccurrence(row, source, 'user_message'),
                context,
            );
        case 'watch':
            return registry.watch.materialize(
                source.payload,
                registryOccurrence(row, source, 'watch'),
                context,
            );
        case 'domain_event_observed':
            return registry.domain_event_observed.materialize(
                source.payload,
                registryOccurrence(row, source, 'domain_event_observed'),
                context,
            );
        default:
            return null;
    }
}

function materializeMember(
    registry: ActivityProjectionRegistry,
    eventType: KnownEventType,
    row: ProjectedTranscriptRow,
    source: OccurrenceSource,
): AnyMemberMaterialization | null {
    switch (eventType) {
        case 'artifact_updated':
            return registry.artifact_updated.materialize(
                source.payload,
                registryOccurrence(row, source, 'artifact_updated'),
            );
        case 'tool_approval_denied':
            return registry.tool_approval_denied.materialize(
                source.payload,
                registryOccurrence(row, source, 'tool_approval_denied'),
            );
        case 'tool_approval_granted':
            return registry.tool_approval_granted.materialize(
                source.payload,
                registryOccurrence(row, source, 'tool_approval_granted'),
            );
        case 'tool_approval_required':
            return registry.tool_approval_required.materialize(
                source.payload,
                registryOccurrence(row, source, 'tool_approval_required'),
            );
        case 'tool_output':
            return registry.tool_output.materialize(
                source.payload,
                registryOccurrence(row, source, 'tool_output'),
            );
        case 'user_question_answered':
            return registry.user_question_answered.materialize(
                source.payload,
                registryOccurrence(row, source, 'user_question_answered'),
            );
        case 'user_question_cancelled':
            return registry.user_question_cancelled.materialize(
                source.payload,
                registryOccurrence(row, source, 'user_question_cancelled'),
            );
        case 'user_question_required':
            return registry.user_question_required.materialize(
                source.payload,
                registryOccurrence(row, source, 'user_question_required'),
            );
        default:
            return null;
    }
}

function materializeLifecycleEvidence(
    registry: ActivityProjectionRegistry,
    eventType: KnownEventType,
    row: ProjectedTranscriptRow,
    source: OccurrenceSource,
    context: SafeRenderContext,
): LifecycleEvidenceDelta | null {
    switch (eventType) {
        case 'interrupt_requested': {
            const result = registry.interrupt_requested.materialize(
                source.payload,
                registryOccurrence(row, source, 'interrupt_requested'),
                context,
            );
            return result.kind === 'rendered' ? result.evidence : null;
        }
        case 'run_cancel_requested': {
            const result = registry.run_cancel_requested.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_cancel_requested'),
            );
            return result.kind === 'invisible' ? result.evidence : null;
        }
        case 'run_cancelled': {
            const result = registry.run_cancelled.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_cancelled'),
            );
            return result.kind === 'invisible' ? result.evidence : null;
        }
        case 'run_completed': {
            const result = registry.run_completed.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_completed'),
            );
            return result.kind === 'invisible' ? result.evidence : null;
        }
        case 'run_failed': {
            const result = registry.run_failed.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_failed'),
                context,
            );
            return result.kind === 'rendered' ? result.evidence : null;
        }
        case 'run_interrupted': {
            const result = registry.run_interrupted.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_interrupted'),
            );
            return result.kind === 'invisible' ? result.evidence : null;
        }
        case 'run_started': {
            const result = registry.run_started.materialize(
                source.payload,
                registryOccurrence(row, source, 'run_started'),
            );
            return result.kind === 'invisible' ? result.evidence : null;
        }
        case 'stop_requested': {
            const result = registry.stop_requested.materialize(
                source.payload,
                registryOccurrence(row, source, 'stop_requested'),
                context,
            );
            return result.kind === 'rendered' ? result.evidence : null;
        }
        default:
            return null;
    }
}

function reduceMemberIntoState(
    state: ToolCompoundState,
    slots: ReadonlyArray<
        | CompoundSlotDeclaration<ToolCompoundState, 'approval'>
        | CompoundSlotDeclaration<ToolCompoundState, 'artifacts'>
        | CompoundSlotDeclaration<ToolCompoundState, 'question'>
        | CompoundSlotDeclaration<ToolCompoundState, 'tool_result'>
    >,
    member: AnyParsedSlotMember,
): ToolCompoundState {
    switch (member.slot) {
        case 'approval':
            for (const slot of slots) {
                if (slot.name === 'approval') {
                    return reduceParsedSlotMember(state, slot, member);
                }
            }
            return state;
        case 'artifacts':
            for (const slot of slots) {
                if (slot.name === 'artifacts') {
                    return reduceParsedSlotMember(state, slot, member);
                }
            }
            return state;
        case 'question':
            for (const slot of slots) {
                if (slot.name === 'question') {
                    return reduceParsedSlotMember(state, slot, member);
                }
            }
            return state;
        case 'tool_result':
            for (const slot of slots) {
                if (slot.name === 'tool_result') {
                    return reduceParsedSlotMember(state, slot, member);
                }
            }
            return state;
        default: {
            const exhaustiveMember: never = member;
            return exhaustiveMember;
        }
    }
}

function memberCreatesBoundary(member: AnyParsedSlotMember): boolean {
    switch (member.slot) {
        case 'approval':
        case 'question':
            return true;
        case 'artifacts':
        case 'tool_result':
            return false;
        default: {
            const exhaustiveMember: never = member;
            return exhaustiveMember;
        }
    }
}

function matchingOwnerOccurrenceId(
    projectedMember: ProjectedMember,
    ownerOccurrenceIdByKey: ReadonlyMap<string, string>,
): string | undefined {
    switch (projectedMember.eventType) {
        case 'tool_approval_required':
        case 'user_question_required': {
            const causedByKey = projectedMember.member.keys.find(
                (key) => key.kind === 'event_id',
            );
            if (causedByKey !== undefined) {
                return ownerOccurrenceIdByKey.get(slotKey(causedByKey));
            }
            const commandKey = projectedMember.member.keys.find(
                (key) => key.kind === 'command_id',
            );
            return commandKey === undefined
                ? undefined
                : ownerOccurrenceIdByKey.get(slotKey(commandKey));
        }
        default:
            return projectedMember.member.keys
                .map((key) => ownerOccurrenceIdByKey.get(slotKey(key)))
                .find((candidate) => candidate !== undefined);
    }
}

function noProjectionIssue(_issue: ActivityProjectionIssue): void {}

export function projectActivityOccurrences(
    state: TranscriptState,
    context: SafeRenderContext,
    registry: ActivityProjectionRegistry = ACTIVITY_REGISTRY,
    recordIssue: ActivityProjectionIssueRecorder = noProjectionIssue,
): ActivityOccurrenceProjection {
    const baseRows = projectTranscript(state).rows;
    const sourcesByOccurrenceId = new Map<string, OccurrenceSource>();
    const ownerByOccurrenceId = new Map<string, ToolOwnerRecord>();
    const ownerOccurrenceIdByKey = new Map<string, string>();
    const members: Array<ProjectedMember> = [];
    const lifecycleEvidence: Array<LifecycleEvidenceDelta> = [];
    const issues: Array<ActivityProjectionIssue> = [];
    const issueKeys = new Set<string>();
    const protocolConflicts: Array<CompoundProtocolConflict> = [];
    const boundaryReasons = new Map<string, ActivityBoundaryReason>();
    const malformedOccurrenceIds = new Set<string>();

    function emitIssue(
        reason: ActivityProjectionIssue['reason'],
        eventType: ActivityProjectionIssue['eventType'],
    ): void {
        const key = `${reason}:${eventType ?? 'none'}`;
        if (issueKeys.has(key) || issueKeys.size >= MAX_PROJECTION_ISSUES) {
            return;
        }
        issueKeys.add(key);
        const issue = { reason, eventType, count: 1 } as const;
        issues.push(issue);
        recordIssue(issue);
    }

    function emitRuntimeIssues(
        runtimeIssues: ReadonlyArray<RegistryRuntimeIssue>,
        eventType: KnownEventType,
    ): void {
        for (const issue of runtimeIssues) {
            emitIssue(issue.reason, eventType);
        }
    }

    for (const row of baseRows) {
        const source = occurrenceSource(row, state);
        if (source === null) {
            boundaryReasons.set(row.occurrenceId, 'unknown');
            continue;
        }
        sourcesByOccurrenceId.set(row.occurrenceId, source);
        if (!hasKnownEventType(source.eventType)) {
            emitIssue('unknown_event_type', source.eventType);
            boundaryReasons.set(row.occurrenceId, 'unknown');
            continue;
        }
        const eventType = source.eventType;
        if (!registry[eventType].schema.safeParse(source.payload).success) {
            emitIssue('known_payload_invalid', eventType);
            malformedOccurrenceIds.add(row.occurrenceId);
            continue;
        }
        const lifecycle = materializeLifecycleEvidence(
            registry,
            eventType,
            row,
            source,
            context,
        );
        if (lifecycle !== null) {
            lifecycleEvidence.push(lifecycle);
        }
        if (eventType === 'tool_call') {
            const occurrence = registryOccurrence(row, source, 'tool_call');
            const parsed = registry.tool_call.schema.safeParse(source.payload);
            if (!parsed.success) {
                boundaryReasons.set(row.occurrenceId, 'malformed');
                continue;
            }
            const owner = {
                occurrence,
                source,
                state: registry.tool_call.initialState,
            };
            ownerByOccurrenceId.set(row.occurrenceId, owner);
            for (const key of registry.tool_call.keys(
                parsed.data,
                occurrence,
            )) {
                const serializedKey = slotKey(key);
                if (!ownerOccurrenceIdByKey.has(serializedKey)) {
                    ownerOccurrenceIdByKey.set(serializedKey, row.occurrenceId);
                }
            }
            continue;
        }
        const member = materializeMember(registry, eventType, row, source);
        if (member !== null) {
            if (member.kind === 'member') {
                members.push({ eventType, member: member.member });
                emitRuntimeIssues(member.issues, eventType);
                if (memberCreatesBoundary(member.member)) {
                    boundaryReasons.set(row.occurrenceId, 'invisible');
                }
            } else {
                boundaryReasons.set(row.occurrenceId, 'malformed');
            }
            continue;
        }
        const grouped = materializeGroupedStandalone(
            registry,
            eventType,
            row,
            source,
            context,
        );
        if (grouped === null || grouped.kind === 'malformed') {
            boundaryReasons.set(
                row.occurrenceId,
                grouped === null ? 'invisible' : 'malformed',
            );
        }
    }

    const unappliedMemberIndexes = new Set(
        members.map((_member, index) => index),
    );
    const terminalMemberByOwnerSlot = new Map<string, string>();
    let madeProgress = true;
    while (madeProgress) {
        madeProgress = false;
        for (const index of Array.from(unappliedMemberIndexes)) {
            const projectedMember = members[index];
            if (projectedMember === undefined) {
                continue;
            }
            // Required decision members attach through envelope causality
            // before their command IDs become aliases. Other member key
            // arrays are already ordered by their registry declarations.
            const ownerOccurrenceId = matchingOwnerOccurrenceId(
                projectedMember,
                ownerOccurrenceIdByKey,
            );
            if (ownerOccurrenceId === undefined) {
                continue;
            }
            const owner = ownerByOccurrenceId.get(ownerOccurrenceId);
            if (owner === undefined) {
                continue;
            }
            if (
                owner.state.appliedMemberOccurrenceIds.has(
                    projectedMember.member.memberOccurrenceId,
                )
            ) {
                unappliedMemberIndexes.delete(index);
                madeProgress = true;
                continue;
            }
            const terminalKey = `${ownerOccurrenceId}:${projectedMember.member.slot}`;
            const existingTerminalMemberId =
                terminalMemberByOwnerSlot.get(terminalKey);
            if (
                projectedMember.member.terminal &&
                existingTerminalMemberId !== undefined &&
                existingTerminalMemberId !==
                    projectedMember.member.memberOccurrenceId
            ) {
                protocolConflicts.push({
                    reason: 'terminal_slot_conflict',
                    slot: projectedMember.member.slot,
                });
                unappliedMemberIndexes.delete(index);
                madeProgress = true;
                continue;
            }
            const nextState = reduceMemberIntoState(
                owner.state,
                registry.tool_call.slots,
                projectedMember.member,
            );
            ownerByOccurrenceId.set(ownerOccurrenceId, {
                ...owner,
                state: nextState,
            });
            if (projectedMember.member.terminal) {
                terminalMemberByOwnerSlot.set(
                    terminalKey,
                    projectedMember.member.memberOccurrenceId,
                );
            }
            for (const key of projectedMember.member.keys) {
                ownerOccurrenceIdByKey.set(slotKey(key), ownerOccurrenceId);
            }
            unappliedMemberIndexes.delete(index);
            madeProgress = true;
        }
    }

    if (
        state.lastDoneReason === 'cycle_complete' &&
        unappliedMemberIndexes.size > 0
    ) {
        emitIssue('orphan_slot', null);
    }

    applyLocalSupersession(state, ownerByOccurrenceId);

    const occurrences: Array<ProjectedActivityInput> = [];
    for (const row of baseRows) {
        const source = sourcesByOccurrenceId.get(row.occurrenceId);
        if (source === undefined) {
            continue;
        }
        const day = organizationDay(
            source.createdAt,
            state.scope.organizationTimeZone,
        );
        if (!hasKnownEventType(source.eventType)) {
            occurrences.push({
                kind: 'boundary',
                reason: 'unknown',
                rowId: `boundary:${row.occurrenceId}`,
                occurrenceId: row.occurrenceId,
                eventIds: source.eventIds,
                day,
            });
            continue;
        }
        const eventType = source.eventType;
        if (malformedOccurrenceIds.has(row.occurrenceId)) {
            const malformedPolicy = registry[eventType].malformed;
            if (malformedPolicy.kind === 'placeholder') {
                occurrences.push({
                    kind: 'placeholder',
                    rowId: row.occurrenceId,
                    occurrenceId: row.occurrenceId,
                    eventIds: source.eventIds,
                    day,
                    customerCopy: malformedPolicy.customerCopy,
                });
            } else {
                occurrences.push({
                    kind: 'boundary',
                    reason: 'malformed',
                    rowId: `boundary:${row.occurrenceId}`,
                    occurrenceId: row.occurrenceId,
                    eventIds: source.eventIds,
                    day,
                });
            }
            continue;
        }
        if (eventType === 'tool_call') {
            const owner = ownerByOccurrenceId.get(row.occurrenceId);
            if (owner === undefined) {
                occurrences.push({
                    kind: 'placeholder',
                    rowId: row.occurrenceId,
                    occurrenceId: row.occurrenceId,
                    eventIds: source.eventIds,
                    day,
                    customerCopy:
                        registry.tool_call.malformed.kind === 'placeholder'
                            ? registry.tool_call.malformed.customerCopy
                            : 'Working on the request',
                });
                continue;
            }
            const materialized = registry.tool_call.materialize(
                source.payload,
                owner.state,
                owner.occurrence,
                context,
            );
            if (
                materialized.kind === 'malformed' ||
                materialized.group === null
            ) {
                occurrences.push({
                    kind: 'boundary',
                    reason:
                        materialized.kind === 'malformed'
                            ? 'malformed'
                            : 'invisible',
                    rowId: `boundary:${row.occurrenceId}`,
                    occurrenceId: row.occurrenceId,
                    eventIds: source.eventIds,
                    day,
                });
                continue;
            }
            emitRuntimeIssues(materialized.issues, eventType);
            occurrences.push({
                kind: 'activity_occurrence',
                rowId: row.occurrenceId,
                occurrenceId: row.occurrenceId,
                eventIds: source.eventIds,
                day,
                createdAtMs: occurrenceCreatedAtMs(source.createdAt),
                node: materialized.node,
                group: materialized.group,
            });
            continue;
        }
        const grouped = materializeGroupedStandalone(
            registry,
            eventType,
            row,
            source,
            context,
        );
        if (
            grouped !== null &&
            grouped.kind === 'rendered' &&
            grouped.group !== null
        ) {
            emitRuntimeIssues(grouped.issues, eventType);
            occurrences.push({
                kind: 'activity_occurrence',
                rowId: row.occurrenceId,
                occurrenceId: row.occurrenceId,
                eventIds: source.eventIds,
                day,
                createdAtMs: occurrenceCreatedAtMs(source.createdAt),
                node: grouped.node,
                group: grouped.group,
            });
            continue;
        }
        if (grouped !== null && grouped.kind === 'malformed') {
            if (grouped.policy.kind === 'placeholder') {
                occurrences.push({
                    kind: 'placeholder',
                    rowId: row.occurrenceId,
                    occurrenceId: row.occurrenceId,
                    eventIds: source.eventIds,
                    day,
                    customerCopy: grouped.policy.customerCopy,
                });
            } else {
                occurrences.push({
                    kind: 'boundary',
                    reason: 'malformed',
                    rowId: `boundary:${row.occurrenceId}`,
                    occurrenceId: row.occurrenceId,
                    eventIds: source.eventIds,
                    day,
                });
            }
            continue;
        }
        const ordinary = materializeOrdinaryStandalone(
            registry,
            eventType,
            row,
            source,
            context,
        );
        if (ordinary?.kind === 'rendered') {
            emitRuntimeIssues(ordinary.issues, eventType);
            const messageSender = messageSenderFor(eventType, source, context);
            const messageText =
                eventType === 'assistant_text'
                    ? assistantTextPayloadSchema.safeParse(source.payload).data
                          ?.text
                    : undefined;
            occurrences.push({
                kind: 'ordinary_occurrence',
                rowId: row.occurrenceId,
                occurrenceId: row.occurrenceId,
                eventIds: source.eventIds,
                day,
                node: ordinary.node,
                ...(messageSender === null ? {} : { messageSender }),
                ...(messageText === undefined ? {} : { messageText }),
            });
            continue;
        }
        if (
            ordinary?.kind === 'malformed' &&
            ordinary.policy.kind === 'placeholder'
        ) {
            occurrences.push({
                kind: 'placeholder',
                rowId: row.occurrenceId,
                occurrenceId: row.occurrenceId,
                eventIds: source.eventIds,
                day,
                customerCopy: ordinary.policy.customerCopy,
            });
            continue;
        }
        if (boundaryReasons.has(row.occurrenceId)) {
            occurrences.push({
                kind: 'boundary',
                reason: boundaryReasons.get(row.occurrenceId) ?? 'unknown',
                rowId: `boundary:${row.occurrenceId}`,
                occurrenceId: row.occurrenceId,
                eventIds: source.eventIds,
                day,
            });
        }
    }

    return {
        occurrences,
        compoundStateByOwnerOccurrenceId: new Map(
            Array.from(ownerByOccurrenceId, ([occurrenceId, owner]) => [
                occurrenceId,
                owner.state,
            ]),
        ),
        lifecycleEvidence,
        issues,
        protocolConflicts,
    };
}
